import { useEffect, useState } from 'react';

const DEFAULT_CONFIG = {
  weeksAhead: 4,
  includePastWeeks: 0,
  groupBy: 'month',
  showLocations: true
};

export default function EventCalendarSectionBuilder({ config = {}, onConfigChange }) {
  const [weeksAhead, setWeeksAhead] = useState(DEFAULT_CONFIG.weeksAhead);
  const [includePastWeeks, setIncludePastWeeks] = useState(DEFAULT_CONFIG.includePastWeeks);
  const [groupBy, setGroupBy] = useState(DEFAULT_CONFIG.groupBy);
  const [showLocations, setShowLocations] = useState(DEFAULT_CONFIG.showLocations);

  useEffect(() => {
    setWeeksAhead(config.weeksAhead ?? DEFAULT_CONFIG.weeksAhead);
    setIncludePastWeeks(config.includePastWeeks ?? DEFAULT_CONFIG.includePastWeeks);
    setGroupBy(config.groupBy ?? DEFAULT_CONFIG.groupBy);
    setShowLocations(config.showLocations ?? DEFAULT_CONFIG.showLocations);
  }, [config]);

  useEffect(() => {
    onConfigChange({ weeksAhead, includePastWeeks, groupBy, showLocations });
  }, [weeksAhead, includePastWeeks, groupBy, showLocations, onConfigChange]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Show Upcoming</label>
          <input
            type="range"
            min="2"
            max="12"
            value={weeksAhead}
            onChange={(e) => setWeeksAhead(Number(e.target.value))}
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">Displaying next {weeksAhead} weeks of events.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Include Past Weeks</label>
          <input
            type="range"
            min="0"
            max="4"
            value={includePastWeeks}
            onChange={(e) => setIncludePastWeeks(Number(e.target.value))}
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">Include {includePastWeeks} week(s) of history for context.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Grouping</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="month">By Month</option>
            <option value="week">By Week</option>
            <option value="day">Single List</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Choose how events are grouped in the portal view.</p>
        </div>

        <div className="flex flex-col justify-between">
          <label className="inline-flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={showLocations}
              onChange={(e) => setShowLocations(e.target.checked)}
              className="rounded border-gray-300"
            />
            Display event locations
          </label>
          <p className="text-xs text-gray-500">
            Disable this for travel teams who keep rink locations private.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <h4 className="text-xs font-semibold text-blue-800 mb-1">How it works</h4>
        <p className="text-xs text-blue-700">
          The event calendar pulls from your schedule in real-time. Adjust the range to show a full monthly grid or a
          rolling agenda for parents. Grouping by month works best for desktop, while week view offers a concise mobile-friendly layout.
        </p>
      </div>
    </div>
  );
}

